import React, { useState } from 'react';
import { Sparkles, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function Footer() {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubscribing(true);

    try {
      await axios.post(`${API}/api/newsletter/subscribe`, { email });
      toast.success('Thank you for subscribing! Check your email for confirmation.');
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-slate-900 py-20 mt-24">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid md:grid-cols-5 gap-12 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                <Sparkles className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-xl font-semibold text-white">Learn with Zeenat</span>
            </div>
            <p className="text-slate-400 text-base leading-relaxed mb-6">
              Helping primary teachers save time and help every child learn with AI-powered, curriculum-aligned teaching resources.
            </p>
            <p className="text-slate-500 flex items-center gap-2 text-sm mb-6">
              Made with <Heart className="w-4 h-4 text-red-400 fill-current" /> by an experienced teacher
            </p>
            
            <div className="mb-6">
              <h5 className="text-white font-semibold mb-3 text-sm">Subscribe to Weekly Tips</h5>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  disabled={subscribing}
                  className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-50"
                />
                <button 
                  type="submit"
                  disabled={subscribing}
                  className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {subscribing ? 'Joining...' : 'Join'}
                </button>
              </form>
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Platform</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/ai-tools" className="text-slate-400 hover:text-white transition-colors text-sm">
                  AI Tools
                </Link>
              </li>
              <li>
                <Link to="/browse" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Worksheets
                </Link>
              </li>
              <li>
                <Link to="/browse" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Test Papers
                </Link>
              </li>
              <li>
                <Link to="/browse" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Lesson Plans
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Resources</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/teacher-tips" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Teacher Tips
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-slate-400 hover:text-white transition-colors text-sm">
                  About
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Admin Login
                </Link>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-slate-800 pt-8">
          <p className="text-slate-500 text-sm text-center">
            © {new Date().getFullYear()} Learn with Zeenat. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
